import { useEffect, useState } from "react";
import { api } from "../api.ts";
import { AvisoError, useAccion } from "../pantallas/comun.tsx";

export interface EstadoIA {
  configurada: boolean; origen: "panel" | "servidor" | null; ultimoOk: string | null; fallosSeguidos: number;
  ultimoFallo: { fecha: string; motivo: string; codigo: number | null } | null;
  nivel: "ok" | "sin_clave" | "sin_uso" | "problema"; titulo: string; detalle: string;
}

/** Estado de la IA (clave de Anthropic) para el dueño de la plataforma; se vuelve a consultar cada 5 minutos. */
export function useEstadoIA(activo = true): EstadoIA | null {
  const [e, setE] = useState<EstadoIA | null>(null);
  useEffect(() => {
    if (!activo) return;
    let vivo = true;
    const cargar = () => api<EstadoIA>("GET", "/api/plataforma/ia").then((r) => { if (vivo) setE(r); }).catch(() => undefined);
    cargar();
    const t = setInterval(cargar, 5 * 60 * 1000);
    return () => { vivo = false; clearInterval(t); };
  }, [activo]);
  return e;
}

const cuando = (iso: string) => new Date(iso).toLocaleString("es-EC", { dateStyle: "medium", timeStyle: "short" });

/** Aviso del Panel Donna: dice si la IA está respondiendo y deja poner, probar o quitar la clave de Anthropic sin entrar al servidor. */
export function AvisoIA() {
  const inicial = useEstadoIA();
  const [nuevo, setNuevo] = useState<EstadoIA | null>(null);
  const e = nuevo ?? inicial;
  const [clave, setClave] = useState("");
  const [msg, setMsg] = useState("");
  const a = useAccion();
  if (!e) return null;
  const guardar = async () => {
    setMsg("");
    const r = await a.correr(() => api<EstadoIA>("PUT", "/api/plataforma/ia/clave", { clave: clave.trim() }));
    if (!r) return;
    setClave(""); setNuevo(r);
    const t = await a.correr(() => api<EstadoIA>("POST", "/api/plataforma/ia/probar"));
    if (t) { setNuevo(t); setMsg(t.nivel === "ok" ? "Clave guardada y probada: la IA responde bien." : "Clave guardada, pero la prueba falló. Revisa el aviso de arriba."); }
  };
  const probar = async () => { setMsg(""); const t = await a.correr(() => api<EstadoIA>("POST", "/api/plataforma/ia/probar")); if (t) { setNuevo(t); setMsg(t.nivel === "ok" ? "La IA responde bien." : "La prueba falló. Revisa el aviso."); } };
  const quitar = async () => {
    if (!confirm("¿Quitar la clave puesta en el Panel Donna? Si el servidor tiene una en su archivo de configuración se usa esa; si no, los dictados vuelven a las reglas locales.")) return;
    setMsg(""); const t = await a.correr(() => api<EstadoIA>("DELETE", "/api/plataforma/ia/clave")); if (t) { setNuevo(t); setMsg("Clave quitada del panel."); }
  };
  return (
    <div style={{ display: "grid", gap: 8 }}>
      {e.nivel === "ok"
        ? <p className="muted" style={{ margin: 0, fontSize: 13 }}>IA: responde bien{e.ultimoOk ? ` (último dictado ${cuando(e.ultimoOk)})` : ""}.</p>
        : (
          <div className={`aviso ${e.nivel === "problema" ? "error" : "info"}`} role={e.nivel === "problema" ? "alert" : "status"}>
            <b>{e.titulo}.</b> {e.detalle}
            {e.nivel === "problema" && e.ultimoFallo && <> <span className="muted">Último intento fallido: {cuando(e.ultimoFallo.fecha)}{e.ultimoFallo.codigo ? ` · código ${e.ultimoFallo.codigo}` : ""}{e.fallosSeguidos > 1 ? ` · ${e.fallosSeguidos} seguidos` : ""}.</span></>}
          </div>
        )}
      <details className="nuevo-control" open={e.nivel === "sin_clave" || e.nivel === "problema"}>
        <summary>Clave de IA (Anthropic){e.origen ? ` · puesta en ${e.origen === "panel" ? "el Panel Donna" : "el servidor"}` : " · sin clave"}</summary>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Créala en platform.claude.com (API keys), cópiala y pégala aquí. Se guarda cifrada y nunca se vuelve a mostrar. Mientras no haya clave, los dictados usan las reglas locales.</p>
        <label className="campo">Clave nueva
          <input className="entrada mono" type="password" autoComplete="off" spellCheck={false} value={clave} onChange={(ev) => setClave(ev.target.value)} placeholder="sk-ant-…" aria-label="Clave de Anthropic" />
        </label>
        <AvisoError error={a.error} />
        {msg && <div className="aviso ok" role="status">{msg}</div>}
        <div className="fila">
          <button className="btn pri" disabled={a.ocupado || !clave.trim()} onClick={guardar}>Guardar y probar</button>
          <button className="btn" disabled={a.ocupado || !e.configurada} onClick={probar}>Probar ahora</button>
          {e.origen === "panel" && <button className="btn peligro" disabled={a.ocupado} onClick={quitar}>Quitar la clave del panel</button>}
        </div>
      </details>
    </div>
  );
}

interface Uso {
  mes: string; meses: string[]; precios: { entrada: number | null; salida: number | null };
  clinicas: { id: string; nombre: string; plan: string; dictados: number; llamadas: number; entrada: number; salida: number; costo: number | null; costoPorDictado: number | null }[];
  total: { dictados: number; llamadas: number; entrada: number; salida: number; costo: number | null; costoPorDictado: number | null };
}
const miles = (n: number) => n.toLocaleString("es-EC");
const usd = (n: number | null, dec = 2) => (n === null ? "—" : `$${n.toLocaleString("es-EC", { minimumFractionDigits: dec, maximumFractionDigits: dec })}`);
const NOMBRE_MES = (m: string) => { const [y, mm] = m.split("-"); return `${["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"][Number(mm) - 1] ?? mm} ${y}`; };

/** Panel Donna → Uso de IA: dictados, tokens y costo estimado por clínica y mes, para fijar precios y límites de los planes. */
export function UsoIA() {
  const [mes, setMes] = useState("");
  const [u, setU] = useState<Uso | null>(null);
  const [pe, setPe] = useState(""), [ps, setPs] = useState("");
  const [msg, setMsg] = useState("");
  const a = useAccion();
  const cargar = (m: string) => api<Uso>("GET", `/api/plataforma/ia/uso${m ? `?mes=${m}` : ""}`).then((r) => {
    setU(r); setMes(r.mes); setPe(r.precios.entrada === null ? "" : String(r.precios.entrada)); setPs(r.precios.salida === null ? "" : String(r.precios.salida));
  }).catch(a.setError);
  useEffect(() => { cargar(""); }, []);
  if (!u) return <div style={{ display: "grid", gap: 8 }}><AvisoError error={a.error} />{!a.error && <p className="muted">Cargando…</p>}</div>;
  const guardarPrecios = async () => {
    setMsg("");
    const r = await a.correr(() => api("PUT", "/api/plataforma/ia/precios", { entrada: pe.trim(), salida: ps.trim() }));
    if (r) { setMsg("Precios guardados."); cargar(mes); }
  };
  return (
    <div style={{ display: "grid", gap: 14 }}>
      <div className="fila" style={{ alignItems: "flex-end", flexWrap: "wrap" }}>
        <label className="campo" style={{ flex: "0 1 220px" }}>Mes
          <select className="entrada" value={mes} onChange={(e) => cargar(e.target.value)}>{u.meses.map((m) => <option key={m} value={m}>{NOMBRE_MES(m)}</option>)}</select>
        </label>
        <p className="muted" style={{ margin: 0, fontSize: 13, flex: "1 1 280px" }}>Cuenta las respuestas de la IA desde que se activó este contador. Los tokens de un dictado dependen de lo largo que sea lo que se dicta.</p>
      </div>
      <div className="tiles">
        <div className="tile"><span>Dictados</span><b>{miles(u.total.dictados)}</b><small>{miles(u.total.llamadas)} respuestas de la IA</small></div>
        <div className="tile"><span>Tokens</span><b>{miles(u.total.entrada + u.total.salida)}</b><small>{miles(u.total.entrada)} entrada · {miles(u.total.salida)} salida</small></div>
        <div className="tile"><span>Costo estimado</span><b>{usd(u.total.costo)}</b><small>{u.total.costo === null ? "pon los precios abajo" : "según los precios de abajo"}</small></div>
        <div className="tile"><span>Por dictado</span><b>{usd(u.total.costoPorDictado, 4)}</b><small>costo ÷ dictados</small></div>
      </div>
      <div className="tabla-scroll">
        <table className="tabla">
          <thead><tr><th>Clínica</th><th>Plan</th><th>Dictados</th><th>Respuestas</th><th>Tokens entrada</th><th>Tokens salida</th><th>Costo</th><th>Por dictado</th></tr></thead>
          <tbody>
            {u.clinicas.length === 0 && <tr><td colSpan={8} className="muted">Todavía no hay uso de IA en {NOMBRE_MES(u.mes)}.</td></tr>}
            {u.clinicas.map((c) => (
              <tr key={c.id}><td>{c.nombre}</td><td>{c.plan || "—"}</td><td className="mono">{miles(c.dictados)}</td><td className="mono">{miles(c.llamadas)}</td><td className="mono">{miles(c.entrada)}</td><td className="mono">{miles(c.salida)}</td><td className="mono">{usd(c.costo)}</td><td className="mono">{usd(c.costoPorDictado, 4)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>
      <fieldset className="checklist" style={{ display: "grid", gap: 8 }}>
        <legend>Precio de tu modelo (para estimar el costo)</legend>
        <p className="muted" style={{ margin: 0, fontSize: 13 }}>Dólares por <b>millón de tokens</b>, tal como los publica Anthropic en su página de precios para el modelo que usa Donna. El costo real siempre es el de platform.claude.com/cost; esto es una estimación para comparar planes.</p>
        <div className="rejilla" style={{ gridTemplateColumns: "repeat(auto-fill,minmax(200px,1fr))", gap: 12 }}>
          <label className="campo">Entrada (US$ por millón)<input className="entrada mono" inputMode="decimal" value={pe} onChange={(e) => setPe(e.target.value)} placeholder="por ejemplo 3" /></label>
          <label className="campo">Salida (US$ por millón)<input className="entrada mono" inputMode="decimal" value={ps} onChange={(e) => setPs(e.target.value)} placeholder="por ejemplo 15" /></label>
        </div>
        <AvisoError error={a.error} />
        {msg && <div className="aviso ok" role="status">{msg}</div>}
        <div><button className="btn pri" disabled={a.ocupado} onClick={guardarPrecios}>Guardar precios</button></div>
      </fieldset>
    </div>
  );
}
